import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useWeeklyLeagues } from "../../../lib/api/leagues";
import { useMyFantasyFams } from "../../../lib/api/fantasy-fams";
import { useMySubscription } from "../../../lib/api/subscription";
import { CreateFantasyFamModal } from "../../../components/CreateFantasyFamModal";
import type { FantasyFam } from "../../../lib/types";

// /fantasy — the Fantasy Fam home. Surfaces:
//   - My Fantasy Fams (the user's active + settled entries)
//   - Create-Fantasy-Fam CTA (gated by the free-tier 1-active cap)
//   - Public leaderboards (Weekly / Quarterly / Earnings) + Duels
//   - Legacy themed weekly leagues (still functional below)
export function FantasyIndexRoute() {
	const navigate = useNavigate();
	const weekly = useWeeklyLeagues();
	const myFantasyFams = useMyFantasyFams();
	const subscription = useMySubscription();
	const [createOpen, setCreateOpen] = useState(false);

	const activeFams = (myFantasyFams.data?.fantasyFams ?? []).filter(
		(f) => f.status === "active",
	);
	const isPaid = subscription.data?.status === "active";
	const hasActive = activeFams.length > 0;

	return (
		<div className="mx-auto max-w-5xl space-y-8 px-4 py-8">
			<header className="flex items-center justify-between gap-4">
				<div>
					<Link
						to="/"
						className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
					>
						← Dashboard
					</Link>
					<h1 className="mt-1 text-2xl font-bold">Fantasy</h1>
					<p className="text-sm text-[var(--color-muted)]">
						Four ways to compete: Weekly composites Mon → Fri, Quarterly vs
						SPY, Earnings Showdown weeks where reporters score 1.5×, and
						PM-vs-PM Duels.
					</p>
				</div>
				<Link
					to="/power-ranking"
					className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
				>
					Power Ranking
				</Link>
			</header>

			{/* --- My Fantasy Fams --- */}
			<section className="space-y-3">
				<div className="flex items-center justify-between">
					<h2 className="text-sm font-semibold">My Fantasy Fam</h2>
					<button
						type="button"
						onClick={() => setCreateOpen(true)}
						className="rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-sm font-semibold text-black hover:opacity-90"
					>
						+ Create Fantasy Fam
					</button>
				</div>
				{myFantasyFams.isLoading && (
					<p className="text-sm text-[var(--color-muted)]">Loading…</p>
				)}
				{myFantasyFams.data && activeFams.length === 0 && (
					<div className="rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-bg-elev)] p-6 text-center">
						<p className="text-sm font-semibold">No active Fantasy Fam yet</p>
						<p className="mt-1 text-xs text-[var(--color-muted)]">
							{isPaid
								? "Spin up a Fantasy Fam in any cadence — Weekly, Quarterly, or Earnings."
								: "Free tier — pick one cadence + create your Fantasy Fam. Upgrade to compete in all three at once."}
						</p>
					</div>
				)}
				{activeFams.length > 0 && (
					<div className="grid gap-2 sm:grid-cols-2">
						{activeFams.map((ff) => (
							<MyFantasyFamCard key={ff.id} fantasyFam={ff} />
						))}
					</div>
				)}
			</section>

			{/* --- Public leaderboards + Duels --- */}
			<section className="space-y-3">
				<h2 className="text-sm font-semibold">Leaderboards</h2>
				<div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
					<UmbrellaCard
						to="/fantasy/weekly"
						emoji="🏆"
						title="Weekly"
						blurb="Mon → Fri composite. The flywheel."
					/>
					<UmbrellaCard
						to="/fantasy/quarterly"
						emoji="🏅"
						title="Quarterly"
						blurb="90-day lock vs SPY."
					/>
					<UmbrellaCard
						to="/fantasy/earnings"
						emoji="👑"
						title="Earnings"
						blurb="Reporters score 1.5×."
					/>
					<UmbrellaCard
						to="/fantasy/duels"
						emoji="⚔️"
						title="Duels"
						blurb="PM vs PM, public."
					/>
				</div>
			</section>

			{createOpen && (
				<CreateFantasyFamModal
					hasActive={hasActive}
					isPaid={isPaid}
					onClose={() => setCreateOpen(false)}
					onCreated={(result) => {
						setCreateOpen(false);
						void navigate(`/fantasy-fam/${result.fantasyFam.slug}`);
					}}
				/>
			)}

			{weekly.isLoading && (
				<p className="text-sm text-[var(--color-muted)]">Loading leagues…</p>
			)}
			{weekly.isError && (
				<p className="text-sm text-[var(--color-danger)]">
					Could not load weekly leagues.{" "}
					{weekly.error instanceof Error ? weekly.error.message : ""}
				</p>
			)}

			{weekly.data && (
				<>
					{weekly.data.weekStartsAt && weekly.data.weekEndsAt && (
						<p className="text-xs text-[var(--color-muted)]">
							Week {formatDate(weekly.data.weekStartsAt)} →{" "}
							{formatDate(weekly.data.weekEndsAt)}
						</p>
					)}
					<div className="grid gap-4 sm:grid-cols-2">
						{weekly.data.leagues.map((l) => {
							// When the weekly worker hasn't opened a theme yet the row has
							// no id — surface a disabled state with the same metadata so
							// the user knows it's coming.
							const hasId = l.id !== "";
							const target = hasId
								? `/fantasy/${encodeURIComponent(l.id)}`
								: undefined;

							const Wrapper = ({
								children,
							}: {
								children: React.ReactNode;
							}) =>
								target ? (
									<Link
										to={target}
										className="block rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4 transition hover:border-[var(--color-accent)]"
									>
										{children}
									</Link>
								) : (
									<div className="block rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-bg-card)] p-4 opacity-70">
										{children}
									</div>
								);

							return (
								<Wrapper key={l.theme}>
									<div className="flex items-start justify-between gap-3">
										<div className="flex-1">
											<h3 className="text-lg font-semibold">
												{l.themeName}
											</h3>
											<p className="mt-1 text-xs text-[var(--color-muted)]">
												{l.description}
											</p>
										</div>
										<span className="rounded-md border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-muted)]">
											{l.status}
										</span>
									</div>
									<div className="mt-3 flex items-center justify-between text-xs text-[var(--color-muted)]">
										<span>{l.rosterSize} symbols in roster</span>
										<span>{l.entries} entries</span>
									</div>
									{!hasId && (
										<p className="mt-2 text-xs text-[var(--color-muted)]">
											Opening soon — week worker hasn't seeded this
											theme yet.
										</p>
									)}
								</Wrapper>
							);
						})}
					</div>
				</>
			)}

			<footer className="border-t border-[var(--color-border)] pt-4 text-center text-xs text-[var(--color-muted)]">
				Simulated paper trade. Not investment advice. See{" "}
				<a
					href="https://hedgefam.com/disclosures"
					className="underline hover:text-[var(--color-fg)]"
				>
					disclosures
				</a>
				.
			</footer>
		</div>
	);
}

function UmbrellaCard({
	to,
	emoji,
	title,
	blurb,
}: {
	to: string;
	emoji: string;
	title: string;
	blurb: string;
}) {
	return (
		<Link
			to={to}
			className="flex flex-col gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-3 transition hover:border-[var(--color-accent)]"
		>
			<div className="flex items-center gap-2">
				<span aria-hidden className="text-lg">
					{emoji}
				</span>
				<span className="text-sm font-semibold">Fantasy Fam: {title}</span>
			</div>
			<span className="text-xs text-[var(--color-muted)]">{blurb}</span>
		</Link>
	);
}

function MyFantasyFamCard({ fantasyFam }: { fantasyFam: FantasyFam }) {
	const cadenceLabel =
		fantasyFam.cadence === "weekly"
			? "Weekly"
			: fantasyFam.cadence === "quarterly"
				? "Quarterly"
				: "Earnings";
	const symbolCount = Object.keys(fantasyFam.composite).length;
	return (
		<Link
			to={`/fantasy-fam/${fantasyFam.slug}`}
			className="block rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4 transition hover:border-[var(--color-accent)]"
		>
			<div className="flex items-start justify-between gap-3">
				<div className="flex-1 min-w-0">
					<p className="truncate text-base font-semibold">{fantasyFam.name}</p>
					<p className="mt-0.5 text-xs text-[var(--color-muted)]">
						{cadenceLabel} · {fantasyFam.periodKey}
					</p>
				</div>
				<span className="rounded-md border border-[var(--color-accent)]/40 bg-[var(--color-accent-soft)] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[var(--color-accent)]">
					{fantasyFam.status}
				</span>
			</div>
			<p className="mt-2 text-xs text-[var(--color-muted)]">
				{symbolCount === 0
					? "No picks yet — open to submit your composite."
					: `${symbolCount} symbol${symbolCount === 1 ? "" : "s"} locked.`}
			</p>
		</Link>
	);
}

function formatDate(iso: string): string {
	// Guard the invalid-input path explicitly — `new Date(undefined)` doesn't
	// throw, it returns an Invalid Date whose toLocaleDateString is the
	// literal string "Invalid Date". A try/catch wouldn't help here.
	if (!iso) return "";
	const d = new Date(iso);
	if (!Number.isFinite(d.getTime())) return "";
	return d.toLocaleDateString(undefined, {
		month: "short",
		day: "numeric",
	});
}
